// src/app/core/services/drawer.service.ts

import { Injectable, signal, TemplateRef, Type, ViewContainerRef, effect, untracked } from '@angular/core';
import { InvestmentCreateComponent } from 'src/app/features/investment/pages/create/investment-create.component';

export interface DrawerAction {
  label: string;
  icon?: string;
  severity?: 'primary' | 'secondary' | 'success' | 'info' | 'warn' | 'danger' | 'contrast';
  disabled?: boolean;
  handler: () => void;
}

export interface DrawerContentConfig {
  title: string;
  component?: Type<any>;
  template?: TemplateRef<any>;
  data?: any;
  width?: string;
  showCloseButton?: boolean;
  actions?: DrawerAction[];
  // Enhanced options
  backdrop?: boolean;
  dismissible?: boolean;
  position?: 'left' | 'right' | 'top' | 'bottom';
  modal?: boolean;
}

/**
 * Enhanced DrawerService with better architecture and error handling
 */
@Injectable({
  providedIn: 'root'
})
export class DrawerService {
  private _isVisible = signal(false);
  private _config = signal<DrawerContentConfig | null>(null);
  private _viewContainerRef: ViewContainerRef | null = null;
  private _isInitialized = signal(false);

  // Public readonly signals
  readonly isVisible = this._isVisible.asReadonly();
  readonly config = this._config.asReadonly();
  readonly isInitialized = this._isInitialized.asReadonly();

  constructor() {
    // Auto-cleanup when drawer becomes invisible
    effect(() => {
      const visible = this._isVisible();
      if (!visible && this._viewContainerRef) {
        // Clear container when drawer is hidden
        untracked(() => {
          this.clearContainer();
        });
      }
    });
  }

  /**
   * Initialize service with ViewContainerRef
   * Should be called from DrawerComponent after view init
   */
  setViewContainerRef(vcr: ViewContainerRef): void {
    this._viewContainerRef = vcr;
    this._isInitialized.set(true);
  }

  /**
   * Open drawer with Angular component
   * Component creation handled by DrawerComponent via effect
   */
  openWithComponent<T>(
    component: Type<T>, 
    config: Partial<DrawerContentConfig> = {}
  ): void {
    if (!this._isInitialized()) {
      console.warn('DrawerService not initialized. Make sure DrawerComponent is in DOM.');
      return;
    }

    this.close(); // Cleanup previous content
    
    const finalConfig: DrawerContentConfig = {
      title: 'Quick Action',
      component,
      width: '400px',
      showCloseButton: true,
      backdrop: true,
      dismissible: true,
      position: 'left',
      modal: true,
      ...config
    };

    this._config.set(finalConfig);
    this._isVisible.set(true);
  }

  /**
   * Open drawer with template
   */
  openWithTemplate(
    template: TemplateRef<any>, 
    config: Partial<DrawerContentConfig> = {}
  ): void {
    if (!this._isInitialized()) {
      console.warn('DrawerService not initialized. Make sure DrawerComponent is in DOM.');
      return;
    }

    this.close(); // Cleanup previous content
    
    const finalConfig: DrawerContentConfig = {
      title: 'Quick Action',
      template,
      width: '400px',
      showCloseButton: true,
      backdrop: true,
      dismissible: true,
      position: 'left',
      modal: true,
      ...config
    };

    this._config.set(finalConfig);
    this._isVisible.set(true);
  }

  /**
   * Close drawer with proper cleanup
   */
  close(): void {
    this._isVisible.set(false);
    // Config and container will be cleared by effect
    // Small delay to allow animation
    setTimeout(() => {
      if (!this._isVisible()) {
        this._config.set(null);
      }
    }, 300);
  }

  /**
   * Toggle drawer visibility
   */
  toggle(): void {
    if (this._isVisible()) {
      this.close();
    } else {
      console.warn('Cannot open drawer without configuration. Use openWithComponent() or openWithTemplate().');
    }
  }

  /**
   * Clear ViewContainer safely
   */
  private clearContainer(): void {
    try {
      if (this._viewContainerRef) {
        this._viewContainerRef.clear();
      }
    } catch (error) {
      console.error('Error clearing drawer container:', error);
    }
  }

  /**
   * Check if drawer can be opened
   */
  canOpen(): boolean {
    return this._isInitialized() && this._viewContainerRef !== null;
  }

  // ===========================================
  // QUICK ACTION METHODS
  // ===========================================
  // These will be implemented with actual form components

  /**
   * Quick add investment form
   */
  openQuickAddInvestment(data?: Partial<any>): void {
    console.log('Quick Add Investment - implement with InvestmentQuickFormComponent');
    
    this.openWithComponent(InvestmentCreateComponent, {
      title: 'Dodaj nową inwestycję',
      width: '500px',
      data: { ...data },
      actions: [
        {
          label: 'Anuluj',
          severity: 'secondary',
          handler: () => this.close()
        },
        {
          label: 'Zapisz',
          severity: 'primary',
          handler: () => {}
        }
      ]
    });
  }

  /**
   * Quick add apartment form
   */
  openQuickAddApartment(investmentId: number, data?: Partial<any>): void {
    console.log('Quick Add Apartment - implement with ApartmentQuickFormComponent');
    
    // Example implementation:
    // this.openWithComponent(ApartmentQuickFormComponent, {
    //   title: 'Dodaj nowy apartament',
    //   width: '600px',
    //   data: { 
    //     investmentId,
    //     ...data 
    //   }
    // });
  }

  /**
   * Quick add parking form
   */
  openQuickAddParking(investmentId: number, data?: Partial<any>): void {
    console.log('Quick Add Parking - implement with ParkingQuickFormComponent');
    
    // Example implementation:
    // this.openWithComponent(ParkingQuickFormComponent, {
    //   title: 'Dodaj nowe miejsce parkingowe',
    //   width: '500px',
    //   data: { 
    //     investmentId,
    //     ...data 
    //   }
    // });
  }

  /**
   * Open edit mode for entities
   */
  openEditMode(
    entityType: 'investment' | 'apartment' | 'parking', 
    entityId: number, 
    data?: any
  ): void {
    const titles = {
      investment: 'Edytuj inwestycję',
      apartment: 'Edytuj apartament', 
      parking: 'Edytuj parking'
    };

    console.log(`Edit ${entityType} ${entityId} - implement with ${entityType}EditFormComponent`);
    
    // Example implementation:
    // const componentMap = {
    //   investment: InvestmentEditFormComponent,
    //   apartment: ApartmentEditFormComponent,
    //   parking: ParkingEditFormComponent
    // };
    
    // this.openWithComponent(componentMap[entityType], {
    //   title: titles[entityType],
    //   width: entityType === 'apartment' ? '700px' : '500px',
    //   data: {
    //     entityId,
    //     entityType,
    //     ...data
    //   }
    // });
  }

  // ===========================================
  // SPECIALIZED DRAWER TYPES
  // ===========================================

  /**
   * Open confirmation dialog in drawer
   */
  openConfirmation(
    message: string,
    onConfirm: () => void,
    onCancel?: () => void
  ): void {
    // This would use a ConfirmationComponent
    console.log('Confirmation drawer - implement with ConfirmationComponent');
  }

  /**
   * Open info panel drawer
   */
  openInfoPanel(title: string, content: string): void {
    // This would use an InfoPanelComponent
    console.log('Info panel drawer - implement with InfoPanelComponent');
  }

  /**
   * Open settings drawer
   */
  openSettings(section?: string): void {
    console.log('Settings drawer - implement with SettingsComponent');
  }
}
